import {
  AdTagController,
  type AdTagControllerHost,
  type Constructor,
} from './ad-tag-controller.ts';

type AdsBaseElement = AdTagControllerHost & {
  connectedCallback?(): void;
  disconnectedCallback?(): void;
  attributeChangedCallback?(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void;
};

const DEFAULT_AD_TIMEOUT_MS = 5000;

export function AdTagMixin<TBase extends Constructor<AdsBaseElement>>(
  Base: TBase,
) {
  abstract class AdTagElement extends Base implements AdTagControllerHost {
    static get observedAttributes(): string[] {
      const baseObservedAttributes = (
        Base as TBase & { observedAttributes?: string[] }
      ).observedAttributes || [];

      return baseObservedAttributes.concat([
        'ad-tag-src',
        'vast-src',
        'content-src',
        'ad-timeout',
        'skip-delay',
      ]);
    }

    protected _allowContentPlayOnce = false;
    protected readonly _adsController = new AdTagController(
      this as unknown as AdTagControllerHost,
    );

    constructor(...args: never[]) {
      super(...args);

      if (this.hasAttribute('ad-tag-src') || this.hasAttribute('vast-src')) {
        this._syncAdsControllerConfig();
      }
    }

    connectedCallback(): void {
      super.connectedCallback?.();
      this._syncAdsControllerConfig();
      this._adsController.connect();
    }

    disconnectedCallback(): void {
      super.disconnectedCallback?.();
      this._adsController.disconnect();
    }

    attributeChangedCallback(
      name: string,
      oldVal: string | null,
      newVal: string | null,
    ): void {
      super.attributeChangedCallback?.(name, oldVal, newVal);
      if (
        name === 'ad-tag-src' ||
        name === 'vast-src' ||
        name === 'content-src' ||
        name === 'ad-timeout' ||
        name === 'skip-delay'
      ) {
        this._syncAdsControllerConfig();
      }
    }

    protected _syncAdsControllerConfig(): void {
      this._adsController.configure({
        adTagSrc: this.getAttribute('ad-tag-src') ||
          this.getAttribute('vast-src'),
        contentSrc: this.getAttribute('content-src') ||
          this.getAttribute('src'),
        adTimeout: Number.parseInt(
          this.getAttribute('ad-timeout') || `${DEFAULT_AD_TIMEOUT_MS}`,
          10,
        ),
        // Retained for backward compatibility even though IMA owns skip timing.
        skipDelay: Number.parseInt(
          this.getAttribute('skip-delay') || `${DEFAULT_AD_TIMEOUT_MS}`,
          10,
        ),
      });
    }

    _playContentInternal(): Promise<unknown> {
      return super.play();
    }

    _playContentBypass(): Promise<unknown> {
      this._allowContentPlayOnce = true;
      return super.play();
    }

    _consumePlayBypass(): boolean {
      const allowed = this._allowContentPlayOnce;
      this._allowContentPlayOnce = false;
      return allowed;
    }

    _isPlayBypassed(): boolean {
      return this._allowContentPlayOnce === true;
    }

    override play(): Promise<unknown> {
      if (!this.getAttribute('ad-tag-src') && !this.getAttribute('vast-src')) {
        return super.play();
      }
      if (this._isPlayBypassed()) {
        return super.play();
      }
      return this._adsController.requestContentPlay();
    }
  }

  return AdTagElement;
}
